<template>
  <transition name="modal-view">
    <div
      v-if="visible"
      class="modal-wrap"
      @keyup.esc="requestClose()"
    >
      <div
        class="overlay"
        @click="requestClose()"
      />
        <div
          ref="modal"
          class="modal"
          :class="{ modal__dark: isDarkTheme }"
          role="dialog"
          :aria-labelledby="headingId"
          tabindex="-1"
          @focusout="onFocusOut"
        >
        <div class="modal-inner">
          <button
            v-if="canClose"
            class="close-modal-button"
            type="button"
            :aria-label="$t('ALT_CLOSE_LABEL')"
            @click="requestClose()"
          >
            <slot name="close" />
          </button>
          <h2
            v-if="title"
            :id="headingId"
          >
            {{ title }}
          </h2>
          <slot />
        </div>
        <div class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { v4 as uuid } from 'uuid'
import { createNamespacedHelpers } from 'vuex'

const { mapActions, mapGetters } = createNamespacedHelpers('ui/modals')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  props: {
    name: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    canClose: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      headingId: null
    }
  },

  computed: {
    ...mapGetters(['openModals']),
    ...mapEnvGetters(['isDarkTheme']),

    visible () {
      return this.openModals.includes(this.name)
    },

    isTopModal () {
      return this.visible && this.openModals[this.openModals.length - 1] === this.name
    }
  },

  watch: {
    visible: {
      handler (newVal) {
        this.addBodyStyle(newVal)

        if (newVal) {
          this.setFocus()
        }
      },
      immediate: true
    }
  },

  mounted () {
    this.headingId = `modal-heading-${uuid()}`
  },

  methods: {
    ...mapActions(['open', 'close']),

    requestClose () {
      if (this.canClose) {
        this.close(this.name)
      }
    },

    onFocusOut (ev) {
      if (this.isTopModal) this.setFocus(ev.relatedTarget)
    },

    setFocus (activeElement) {
      if (typeof document !== 'undefined') {
        this.$nextTick(() => {
          const { modal } = this.$refs
          const _activeElement = activeElement || document.activeElement

          if (!_activeElement || (modal && !modal.contains(_activeElement))) {
            this.$refs.modal.focus()
          }
        })
      }
    },

    addBodyStyle (active) {
      if (!this.$isServer) {
        if (active) {
          document.body.classList.add('modal-active')
        } else {
          document.body.classList.remove('modal-active')
        }
      }
    }
  }
}
</script>

<style lang="scss">
$max-modal-width: 360px;

.modal-wrap {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: $z_index_blade;
  pointer-events: none;

  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    width: 100%;
    pointer-events: auto;
  }

  .modal {
    @include shadow(floating);
    position: absolute;
    background: $color-white;
    top: auto;
    left: 50%;
    bottom: 0;
    width: 100%;
    max-height: 100%;
    overflow: auto;
    pointer-events: auto;
    border-radius: 10px 10px 0 0;
    transform: translate3d(-50%, 0, 0);
    overflow-x: hidden;
    overflow-y: auto;

    &:focus {
      outline: none;
    }

    &__dark {
      * {
        color: $color-black;
      }
    }

    @include tablet {
      top: 30%;
      left: 50%;
      bottom: unset;
      max-width: $max-modal-width;
      transform: translate3d(-50%, 0, 0);
      border-radius: 4px;
    }

    .close-modal-button {
      position: absolute;
      background: transparent;
      border: none;
      cursor: pointer;

      &:empty {
        display: none;
      }
    }

    .modal-footer {
      &:empty {
        display: none;
      }

      text-align: center;
    }
  }
}

.modal-active {
  overflow: hidden;
}

.modal-wrap {
  .modal-inner {
    padding: $space-080;
    display: block;

    h2 {
      @include font('060');
      margin-bottom: $space-040;
    }
  }

  .close-modal-button {
    position: absolute;
    right: $space-050;
    top: $space-050;
    padding: 0;
  }

  .overlay {
    background: rgba($color-black, 0.3);
    @include transition-fast;
  }


  .modal-footer {
    padding: 0 $d-med-small $d-small;
  }

  .modal,
  .modal-inner
  ul li,
  fieldset div {
    will-change: auto;
  }
}
</style>
